// ABOUTME: REST hook for the active cart (an offer with status 'cart') and its mutations.
// ABOUTME: Wraps the /api/cpq/cart endpoints plus line-item edits; every call names the cart's dealership.

import { useCallback, useRef, useState } from 'react';

import { REQUEST_DEALERSHIP_HEADER } from '../../dealership/constants';
import { ChainStep } from '../types';
import { OfferDetail } from './useOffer';

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
}

async function apiFetch<T>(url: string, dealership: string, options?: RequestInit): Promise<T> {
    const response = await fetch(url, {
        // The cart's own dealership, always set: an empty value is "none" and keeps the dealership of
        // the screen (an offer, a catalogue) out of the cart calls.
        headers: { 'Content-Type': 'application/json', [REQUEST_DEALERSHIP_HEADER]: dealership },
        ...options,
    });
    const json: ApiResponse<T> = await response.json();
    if (!json.success) {
        throw new Error(json.error ?? 'Unknown error');
    }
    return json.data as T;
}

/** The line count of the user's cart in one dealership (null: the unassigned cart, "none"). */
export interface CartItemCount {
    dealershipId: string | null;
    itemCount: number;
}

export interface ConvertCartPayload {
    title: string;
    customerName: string;
    customerEmail?: string;
}

export interface AddToCartOptions {
    configurationJson?: string;
    prototypeId?: string;
    modelId?: string;
    // Ordered configuration chain, when the catalog configured more than one product for this item.
    // Its last step's configurationJson must match the one above.
    chain?: ChainStep[];
}

/**
 * Hook for active-cart operations via REST API, in the cart's dealership (one cart per dealership).
 * The dealership is read through a ref, so the operations keep their identity when it changes.
 */
export function useCart(dealershipId?: string) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const dealershipRef = useRef(dealershipId ?? '');
    dealershipRef.current = dealershipId ?? '';

    const getActiveCart = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            return await apiFetch<OfferDetail | null>('/api/cpq/cart', dealershipRef.current);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load cart';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const addToCart = useCallback(
        async (productId: string, quantity = 1, options?: AddToCartOptions) => {
            setLoading(true);
            setError(null);
            try {
                return await apiFetch<{ offerId: string; lineItemId: string }>(
                    '/api/cpq/cart/items',
                    dealershipRef.current,
                    {
                        method: 'POST',
                        body: JSON.stringify({ productId, quantity, ...options }),
                    },
                );
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to add to cart';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const discardCart = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await apiFetch('/api/cpq/cart', dealershipRef.current, { method: 'DELETE' });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to discard cart';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const convertCart = useCallback(async (payload: ConvertCartPayload) => {
        setLoading(true);
        setError(null);
        try {
            return await apiFetch<{ offerId: string; offerNumber: string }>(
                '/api/cpq/cart/convert',
                dealershipRef.current,
                {
                    method: 'POST',
                    body: JSON.stringify(payload),
                },
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to convert cart';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateCartItem = useCallback(
        async (cartId: string, lineItemId: string, payload: { quantity?: number }) => {
            setLoading(true);
            setError(null);
            try {
                await apiFetch(
                    `/api/cpq/offers/${cartId}/line-items/${lineItemId}`,
                    dealershipRef.current,
                    {
                        method: 'PUT',
                        body: JSON.stringify(payload),
                    },
                );
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to update item';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const removeCartItem = useCallback(async (cartId: string, lineItemId: string) => {
        setLoading(true);
        setError(null);
        try {
            await apiFetch(
                `/api/cpq/offers/${cartId}/line-items/${lineItemId}`,
                dealershipRef.current,
                {
                    method: 'DELETE',
                },
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to remove item';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // The counts of every dealership's cart: read whatever dealership the call names, so it leaves the
    // loading and error state of the active cart alone.
    const getCartItemCounts = useCallback(
        () => apiFetch<CartItemCount[]>('/api/cpq/cart/counts', dealershipRef.current),
        [],
    );

    return {
        loading,
        error,
        getActiveCart,
        getCartItemCounts,
        addToCart,
        discardCart,
        convertCart,
        updateCartItem,
        removeCartItem,
    };
}
